import React, { useCallback, useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import { IntlProvider } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import LanguageContext from 'contexts/LanguageContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import { ANTD_LOCALES, setMomentLocale } from 'config/antdLocales';
import { LANGUAGES, APP_LANGUAGE_KEYS } from 'config/languages';
import messages from 'config/messages.json';
import { FORM_ERRORS } from 'config/errors';
import { ROLE_SUPER_ADMIN } from 'config/roles';
import { LANG, getFromLocalStorage, writeToLocalState } from 'utils/localStorage';
import { authSelector } from 'store/selectors';
import { getCurrent, logout } from 'store/actions/auth.actions';
import { loadSettings } from 'store/actions/settings.actions';
import { requestCountriesList } from 'store/actions/locations.actions';
import Preloader from 'components/Preloader';

let initialLanguage;
try {
  initialLanguage = getFromLocalStorage(LANG);
  if (!APP_LANGUAGE_KEYS.some((key) => LANGUAGES[key] === initialLanguage)) {
    initialLanguage = LANGUAGES['english'];
  }
} catch (e) {
  initialLanguage = LANGUAGES['english'];
}

setMomentLocale(initialLanguage);

const App = ({ token, getCurrent, loadSettings, logout, children }) => {
  const dispatch = useDispatch()
  const [language, setLanguage] = useState(initialLanguage);
  const [currentUser, setCurrentUser] = useState({});
  const [loading, setLoading] = useState(!!token);

  const changeLanguage = (value) => {
    setLanguage(value);
  }

  useEffect(() => {
    writeToLocalState(LANG, language);
    setMomentLocale(language);
    dispatch(requestCountriesList);
  }, [language])

  const hasPermission = useCallback((permissionToCheck) => {
    const { isSuperAdmin, permissions } = currentUser;
    if (!permissions) return false;
    if (isSuperAdmin) return true;
    if (Array.isArray(permissionToCheck)) {
      return permissionToCheck.some(perm => permissions.includes(perm));
    }
    return permissions.includes(permissionToCheck);
  }, [currentUser])

  useEffect(() => {
    if (!token) {
      setCurrentUser({})
    } else {
      setLoading(true);
      getCurrent()
        .then(data => {
          const { rolesCollection } = data;
          const permissions = rolesCollection.map(({ roleAttributeNames = [] }) => roleAttributeNames).flat();
          const isSuperAdmin = rolesCollection.some(({ role }) => role === ROLE_SUPER_ADMIN);
          setCurrentUser({ ...data, permissions, isSuperAdmin })
        })
        .catch(() => logout())
        .finally(() => setLoading(false));
      loadSettings();
    }
  }, [token])

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      <ConfigProvider
        locale={ANTD_LOCALES[language] || ANTD_LOCALES.en}
        form={{ validateMessages: FORM_ERRORS[language] || FORM_ERRORS.en }}
      >
        <IntlProvider locale={language} messages={messages[language] || messages.en}>
          <CurrentUserContext.Provider value={{ ...currentUser, hasPermission }}>
            {children}
            {loading && (
              <div className="preloader-overlay">
                <Preloader />
              </div>
            )}
          </CurrentUserContext.Provider>
        </IntlProvider>
      </ConfigProvider>
    </LanguageContext.Provider>
  )
};

export default connect(authSelector, { getCurrent, logout, loadSettings })(App);
